/*
 * Productgalerij (sections/product-main.liquid).
 * Hoofdbeelden scrollen horizontaal met snap; thumbnails (desktop) en stippen (mobiel) volgen de
 * positie en sturen hem. Pijlen op de thumbnailstrip verschijnen in de Liquid bij meer dan acht
 * beelden. Klik op een beeld opent de lightbox met zoom; Escape en de knop sluiten hem.
 */
class RmProductGallery extends HTMLElement {
  connectedCallback() {
    this.viewer = this.querySelector('.rm-gallery__viewer');
    this.slides = Array.from(this.querySelectorAll('.rm-gallery__slide'));
    this.thumbs = Array.from(this.querySelectorAll('.rm-gallery__thumb'));
    this.dots = Array.from(this.querySelectorAll('.rm-gallery__dot'));
    this.thumbStrip = this.querySelector('.rm-gallery__thumbs');
    this.lightbox = this.querySelector('.rm-gallery__lightbox');
    this.lightboxImage = this.querySelector('.rm-gallery__lightbox-img');
    this.index = 0;
    if (!this.viewer || this.slides.length === 0) return;

    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i)));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
    this.querySelectorAll('[data-gallery-step]').forEach((button) => {
      button.addEventListener('click', () => this.goTo(this.index + Number(button.dataset.galleryStep)));
    });
    this.querySelectorAll('[data-thumbs-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const step = Number(button.dataset.thumbsStep) * this.thumbStrip.clientHeight * 0.8;
        this.thumbStrip.scrollBy({ top: step, behavior: 'smooth' });
      });
    });

    this.viewer.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.goTo(this.index + 1);
      if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setCurrent(this.slides.indexOf(entry.target));
        });
      },
      { root: this.viewer, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));

    this.querySelectorAll('.rm-gallery__zoom').forEach((button) => {
      button.addEventListener('click', () => this.openLightbox(button));
    });
    if (this.lightbox) {
      this.lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.lightbox.close());
      this.lightbox.addEventListener('click', (event) => {
        if (event.target === this.lightbox) this.lightbox.close();
      });
      this.lightbox.addEventListener('close', () => this.lightbox.classList.remove('is-zoomed'));
      this.lightboxImage?.addEventListener('click', (event) => this.toggleZoom(event));
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  goTo(index) {
    const target = Math.max(0, Math.min(this.slides.length - 1, index));
    const slide = this.slides[target];
    this.viewer.scrollTo({ left: slide.offsetLeft - this.viewer.offsetLeft, behavior: 'smooth' });
    this.setCurrent(target);
  }

  setCurrent(index) {
    if (index < 0 || index === this.index) return;
    this.index = index;
    this.thumbs.forEach((thumb, i) => {
      const current = i === index;
      thumb.classList.toggle('is-current', current);
      thumb.setAttribute('aria-current', current ? 'true' : 'false');
      if (current) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-current', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
    const prev = this.querySelector('[data-gallery-step="-1"]');
    const next = this.querySelector('[data-gallery-step="1"]');
    if (prev) prev.disabled = index === 0;
    if (next) next.disabled = index === this.slides.length - 1;
  }

  openLightbox(button) {
    if (!this.lightbox || !this.lightboxImage) return;
    this.lightboxImage.src = button.dataset.full;
    this.lightboxImage.alt = button.dataset.alt || '';
    this.lightbox.classList.remove('is-zoomed');
    this.lightbox.showModal();
  }

  toggleZoom(event) {
    const zoomed = this.lightbox.classList.toggle('is-zoomed');
    if (zoomed) {
      const rect = this.lightboxImage.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      this.lightboxImage.style.transformOrigin = `${x}% ${y}%`;
    }
  }
}

if (!customElements.get('rm-product-gallery')) {
  customElements.define('rm-product-gallery', RmProductGallery);
}
